import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ChevronDown, Tags, Save, Copy, Loader2 } from 'lucide-react';
import { WORKFLOW_STEPS } from '@/utils/workflowSteps';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CompanyPriceList = ({ company, companies, isRTL, headers }) => {
  const [expanded, setExpanded] = useState(false);
  const [prices, setPrices] = useState({});
  const [openWf, setOpenWf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!company?.id) return;
    axios.get(`${API}/companies/${company.id}/prices`, { headers }).then(r => { setPrices(r.data.prices || {}); setDirty(false); }).catch(() => setPrices({}));
  }, [company?.id]);

  const setPrice = (wf, sub, val) => {
    setPrices(prev => ({ ...prev, [wf]: { ...(prev[wf] || {}), [sub]: val } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/companies/${company.id}/prices`, { prices }, { headers });
      setPrices(r.data.prices || {}); setDirty(false);
      toast.success(isRTL ? 'تم حفظ قائمة الأسعار' : 'Price list saved');
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ في الحفظ' : 'Save failed')); }
    setSaving(false);
  };

  const copy = async () => {
    if (!copyFrom) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/companies/${company.id}/prices/copy-from/${copyFrom}`, {}, { headers });
      setPrices(r.data.prices || {}); setDirty(false); setCopyFrom('');
      toast.success(isRTL ? 'تم نسخ الأسعار' : 'Prices copied');
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'فشل النسخ' : 'Copy failed')); }
    setSaving(false);
  };

  const pricedCount = Object.values(prices).reduce((n, s) => n + Object.values(s || {}).filter(v => parseFloat(v) > 0).length, 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="company-price-list">
      <div className="px-4 py-3 cursor-pointer select-none hover:bg-slate-50/50 transition-colors" onClick={() => setExpanded(!expanded)} data-testid="company-price-list-toggle">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center"><Tags className="w-3.5 h-3.5 text-emerald-600" /></div>
            {isRTL ? 'قائمة الأسعار' : 'Price List'} ({pricedCount})
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={copyFrom} onChange={e => setCopyFrom(e.target.value)} data-testid="price-copy-from-select"
              className="h-8 text-xs border border-slate-200 rounded-lg px-2 bg-white min-w-[200px]">
              <option value="">{isRTL ? '-- نسخ الأسعار من منشأة أخرى --' : '-- Copy prices from company --'}</option>
              {(companies || []).filter(c => c.id !== company.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={copy} disabled={!copyFrom || saving} className="h-8 text-xs gap-1" data-testid="price-copy-btn"><Copy className="w-3 h-3" />{isRTL ? 'نسخ' : 'Copy'}</Button>
            <div className="flex-1" />
            <Button size="sm" onClick={save} disabled={saving || !dirty} className="h-8 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="price-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}{isRTL ? 'حفظ الأسعار' : 'Save'}
            </Button>
          </div>
          <div className="space-y-1.5">
            {Object.entries(WORKFLOW_STEPS).map(([wfKey, wf]) => {
              const wfPrices = prices[wfKey] || {};
              const count = wf.steps.filter(s => parseFloat(wfPrices[s.sub_type]) > 0).length;
              const isOpen = openWf === wfKey;
              return (
                <div key={wfKey} className="rounded-lg border border-slate-100 overflow-hidden">
                  <button type="button" onClick={() => setOpenWf(isOpen ? null : wfKey)} data-testid={`price-wf-${wfKey}`}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-xs font-semibold text-slate-700">
                    <span>{isRTL ? wf.ar : wf.en} <span className="text-slate-400 font-normal">({count}/{wf.steps.length})</span></span>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-slate-50">
                      {wf.steps.map(s => (
                        <div key={s.sub_type} className="flex items-center gap-3 px-3 py-1.5">
                          <span className="text-xs text-slate-600 flex-1">{isRTL ? s.ar : s.en}{s.optional && <span className="text-[9px] text-slate-400 mx-1">({isRTL ? 'اختياري' : 'optional'})</span>}</span>
                          <input type="number" min="0" dir="ltr" value={wfPrices[s.sub_type] ?? ''} placeholder="0"
                            onChange={e => setPrice(wfKey, s.sub_type, e.target.value)} data-testid={`price-input-${wfKey}-${s.sub_type}`}
                            className="w-24 h-7 text-xs text-center font-bold border border-slate-200 rounded-md bg-white focus:border-emerald-500 focus:outline-none" />
                          <span className="text-[10px] text-slate-400 w-8">{isRTL ? 'درهم' : 'AED'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPriceList;
